/**
 * "Add Printer" on a desktop register (Star LAN, Workstream B).
 *
 * Two ways in, one way to save:
 *
 * - **Automatic discovery** searches this register's own network (the main
 *   process's bounded Star status sweep) and lists what answered, with model
 *   and IP address where the printer reports them.
 * - **Add by IP address** is first-class, not a fallback: it is the only way
 *   to reach a printer on another subnet or VLAN.
 *
 * Either way the chosen printer goes through the same check → test page →
 * save step, and is saved as the register's **registered printer** — an
 * endpoint, not a pairing. Mobile never renders this component
 * (`printer-list-dialog.tsx` routes on the printing capability).
 */
import { useEffect } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import MuiPrintIcon from '@mui/icons-material/Print';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { toSavedPrinter, type SavedPrinter } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import LanDiscoveryPanel from './lan-discovery-panel';
import LanCandidatePanel from './lan-candidate-panel';
import { type SetupMode, useLanPrinterSetup } from './use-lan-printer-setup';

// ----------------------------------------------------------------------

export interface LanPrinterDialogProps {
  open: boolean;
  onClose: () => void;
  onSelected: (printer: SavedPrinter) => void;
  currentDefaultId?: string | null;
}

export default function LanPrinterDialog({
  open,
  onClose,
  onSelected,
  currentDefaultId,
}: LanPrinterDialogProps) {
  const setup = useLanPrinterSetup((printer) => {
    onSelected(toSavedPrinter(printer));
    onClose();
  });
  const { mode, scan, candidate, discover, changeMode, discardCandidate } = setup;

  // Search afresh every time the dialog opens: the operator came here to find
  // a printer, and last time's results may describe a network that changed.
  useEffect(() => {
    if (!open) return;
    changeMode('discover');
    discover();
  }, [open, changeMode, discover]);

  const handleClose = () => {
    // Closing mid-check must not leave a trial connection as the active one.
    discardCandidate();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <MuiIcon icon={MuiPrintIcon} />
          </Avatar>
          <Box>
            <Typography variant="h6">{candidate ? 'Check printer' : 'Add Printer'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {candidate
                ? 'Make sure it answers and prints before saving it'
                : 'Star receipt printers on this shop’s network'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {candidate ? (
          <LanCandidatePanel
            candidate={candidate}
            saving={setup.saving}
            onTestPrint={setup.testPrint}
            onSave={setup.save}
            onBack={discardCandidate}
          />
        ) : (
          <>
            <Tabs
              value={mode}
              onChange={(_event, next: SetupMode) => changeMode(next)}
              variant="fullWidth"
              sx={{ px: 3, mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="discover" label="Automatic discovery" />
              <Tab value="manual" label="Add by IP address" />
            </Tabs>

            {mode === 'discover' ? (
              <LanDiscoveryPanel
                scan={scan}
                currentDefaultId={currentDefaultId}
                onSearch={discover}
                onSelect={setup.selectDiscovered}
                onAddByAddress={() => changeMode('manual')}
              />
            ) : (
              <Stack
                component="form"
                spacing={2}
                sx={{ px: 3, pb: 2 }}
                onSubmit={(event) => {
                  event.preventDefault();
                  setup.checkAddress();
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Use this when the printer is on another network, or automatic discovery did not
                  find it. The IP address is printed on the printer’s self-test page.
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  label="Printer IP address"
                  placeholder="192.168.1.50"
                  value={setup.addressInput}
                  onChange={(event) => setup.setAddressInput(event.target.value)}
                  error={setup.addressError !== null}
                  helperText={
                    setup.addressError ??
                    'For a printer that keeps its address, give it a DHCP reservation or a static IP.'
                  }
                  inputProps={{ inputMode: 'decimal', autoComplete: 'off', spellCheck: false }}
                />
                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={setup.addressInput.trim().length === 0}
                  >
                    Check printer
                  </Button>
                </Box>
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      {!candidate && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
